import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { sub } from 'date-fns';
import { ChartType, PlaceValue, TimeInterval } from 'src/app/core/constants/types.constants';
import { ApiService } from 'src/app/core/services/api.service';
import { HistoricalDataPayload } from 'src/app/core/models/payload.model';
import { HistoricalData } from 'src/app/core/models/http.model';
import { EChartsOption } from 'echarts';
import { CurrencyPipe } from '@angular/common';
import { graphic } from 'echarts';
import { PlaceValuePipe } from 'src/app/standalone/pipes/place-value.pipe';
import { getPlaceValue } from 'src/app/core/functions/utility';
import { IxfiThemeService } from 'header';
import { Subscription } from 'rxjs';

/**
 * The e-charts config options are available at the following URL
 * @link https://echarts.apache.org/en/api.html#echartsInstance.showLoading
 * We have only picked out the config options that needed to be changed from the default values
 */
interface EChartLoadTheme {
    color: string;
    text: string;
    fontSize: number;
    textColor: string;
    lineWidth: number;
    maskColor: string;
    fontWeight: string;
    spinnerRadius: number;
}

@Component({
    selector: 'app-coin-chart',
    templateUrl: './coin-charts.component.html',
    styleUrls: ['./coin-charts.component.scss']
})
export class CoinChartsComponent implements OnInit, OnDestroy {
    @Input() coinName!: string;
    @Input() coinSymbol!: string;
    @Input() fixedPrecision!: number;

    /* Date Variables */
    fromDate: number = sub(new Date(), { days: 7 }).valueOf() * 1000000;
    toDate: number = new Date().valueOf() * 1000000;

    /**
     * Chart Variables
     */
    isChartLoading: boolean = false;
    isChartError: boolean = false;

    /* Initialize Chart Tabs */
    chartTabs: { name: string; key: ChartType; label: string }[] = [
        { name: 'GENERAL.PRICE', key: 'price', label: 'Price chart' },
        { name: 'GENERAL.MARKET_CAP', key: 'marketCap', label: 'Market cap chart' },
        { name: 'COIN.CHART.TRADING_VIEW', key: 'tradingView', label: 'Trading view chart' }
    ];

    /* Set default Active tab and Interval  */
    activeTabId: ChartType = 'price';
    selectedInterval: TimeInterval = '7D';

    private apiService = inject(ApiService);
    private currencyPipe = inject(CurrencyPipe);
    private themeService = inject(IxfiThemeService);
    private placeValuePipe = inject(PlaceValuePipe);

    isDarkMode!: boolean;
    themeSub!: Subscription;
    eChartTheme!: 'light' | 'dark';
    eChartLoadTheme!: EChartLoadTheme;

    chartName!: `${string} to USDT Chart`;

    isMcChartValid: boolean = true;
    isCsChartValid: boolean = true;
    isPriceChartValid: boolean = true;

    mcChartOptions!: EChartsOption;
    csChartOptions!: EChartsOption;
    priceChartOptions!: EChartsOption;

    ngOnInit(): void {
        this.chartName = `${this.coinName} to USDT Chart`;
        this.fetchChartData();

        this.themeSub = this.themeService.themeObs.subscribe({
            next: response => {
                this.isDarkMode = response;
                this.eChartTheme = response ? 'dark' : 'light';
                this.setLoadConfigTheme();
                if (this.priceChartOptions) {
                    this.priceChartOptions.tooltip!['borderColor'] = response ? '#525355' : '#a0a7b8';
                    this.priceChartOptions.tooltip!['backgroundColor'] = response ? '#090909' : '#FFFFFF';
                    this.priceChartOptions.tooltip!['textStyle']['color'] = response ? '#EFEFEF' : '#212529';
                }

                if (this.mcChartOptions) {
                    this.mcChartOptions.tooltip!['borderColor'] = response ? '#525355' : '#a0a7b8';
                    this.mcChartOptions.tooltip!['backgroundColor'] = response ? '#090909' : '#FFFFFF';
                    this.mcChartOptions.tooltip!['textStyle']['color'] = response ? '#EFEFEF' : '#212529';
                }
            }
        });
    }

    /**
     * Check this code later
     * This function receives the selected time interval from child component and
     * uses it to fetch new chart data
     * @param timeInterval Selected Time Interval
     */
    changeTimeInterval(timeInterval: TimeInterval) {
        this.selectedInterval = timeInterval;
        const fromDateDictionary = {
            '1D': sub(new Date(), { days: 1 }).valueOf(),
            '7D': sub(new Date(), { days: 7 }).valueOf(),
            '1M': sub(new Date(), { months: 1 }).valueOf(),
            '3M': sub(new Date(), { months: 3 }).valueOf(),
            '1Y': sub(new Date(), { years: 1 }).valueOf()
        };
        this.fromDate = fromDateDictionary[this.selectedInterval] * 1000000;
        this.toDate = new Date().valueOf() * 1000000;
        this.fetchChartData();
    }

    /**
     * This method fetches the selected coin's historical chart data
     */
    fetchChartData() {
        this.isChartLoading = true;

        const payload: HistoricalDataPayload = {
            coinCode: this.coinSymbol,
            fromDate: this.fromDate, // time in nano seconds
            toDate: this.toDate // time in nano seconds
        };

        this.apiService.fetchHistoricalData(payload).subscribe({
            next: response => {
                this.isChartLoading = false;
                if (response.length === 0) this.isChartError = true;
                else {
                    const priceChartData = this._computeChartData(response, 'price');
                    if (!priceChartData.length) this.isPriceChartValid = false;
                    else {
                        // Prepare Price Chart Configuration
                        this.priceChartOptions = {
                            backgroundColor: 'transparent',
                            xAxis: {
                                type: 'time',
                                name: 'Time',
                                nameTextStyle: {
                                    fontWeight: 'bold'
                                },
                                axisLabel: {
                                    show: true,
                                    hideOverlap: true,
                                    formatter: {
                                        year: '{yyyy}',
                                        month: "{MMM}'{yy}",
                                        day: '{dd}',
                                        week: '{ee}'
                                    }
                                }
                            },
                            yAxis: {
                                scale: true,
                                name: 'Price',
                                nameTextStyle: {
                                    fontWeight: 'bold'
                                },
                                type: 'value',
                                splitNumber: 4,
                                boundaryGap: ['10%', '10%'],
                                axisLabel: {
                                    show: true,
                                    fontSize: 11,
                                    hideOverlap: true,
                                    fontWeight: 500,
                                    formatter: (value, _index) => {
                                        if (value > 1000) return `${this.currencyPipe.transform(value, 'USD')}`;
                                        else return `${value.toFixed(this.fixedPrecision)}`;
                                    }
                                }
                            },
                            tooltip: {
                                show: true,
                                trigger: 'axis',
                                showContent: true,
                                textStyle: {
                                    fontSize: 12,
                                    color: this.isDarkMode ? '#EFEFEF' : '#212529'
                                },
                                borderWidth: 1,
                                borderRadius: 4,
                                borderColor: this.isDarkMode ? '#525355' : '#a0a7b8',
                                backgroundColor: this.isDarkMode ? '#090909' : '#FFFFFF',
                                formatter: params => this.getToolTipHtml(params[0])
                            },
                            dataZoom: [
                                {
                                    type: 'slider',
                                    start: 0,
                                    end: 100,
                                    labelFormatter: (value, valueInStr) => new Date(valueInStr).toLocaleString(),
                                    textStyle: {
                                        width: '68',
                                        overflow: 'break',
                                        show: true,
                                        fontSize: 11,
                                        lineHeight: 14
                                    },
                                    backgroundColor: 'transparent',
                                    selectedDataBackground: {
                                        lineStyle: {
                                            width: 1,
                                            color: 'rgb(130, 85, 236)'
                                        },
                                        areaStyle: {
                                            color: new graphic.LinearGradient(0, 0, 0, 1, [
                                                {
                                                    offset: 0,
                                                    color: 'rgba(130, 85, 236, 0.7)'
                                                },
                                                {
                                                    offset: 1,
                                                    color: 'rgba(130, 85, 236, 0.2)'
                                                }
                                            ])
                                        }
                                    }
                                }
                            ],
                            toolbox: {
                                feature: {
                                    restore: {
                                        emphasis: {
                                            iconStyle: {
                                                borderColor: 'rgba(139, 55, 247, 1)',
                                                textFill: 'rgba(139, 55, 247, 1)'
                                            }
                                        }
                                    },
                                    saveAsImage: {
                                        emphasis: {
                                            iconStyle: {
                                                borderColor: 'rgba(139, 55, 247, 1)',
                                                textFill: 'rgba(139, 55, 247, 1)'
                                            }
                                        }
                                    },
                                    dataZoom: {
                                        emphasis: {
                                            iconStyle: {
                                                borderColor: 'rgba(139, 55, 247, 1)',
                                                textFill: 'rgba(139, 55, 247, 1)'
                                            }
                                        }
                                    }
                                }
                            },
                            series: [
                                {
                                    name: 'Price',
                                    type: 'line',
                                    smooth: true,
                                    sampling: 'lttb',
                                    symbol: 'none',
                                    connectNulls: false,
                                    data: priceChartData,
                                    emphasis: {
                                        focus: 'series'
                                    },
                                    lineStyle: {
                                        width: 3,
                                        color: 'rgb(130, 85, 236)'
                                    },
                                    itemStyle: {
                                        // can control the color in the tooltip based on the value
                                        // color: params => {
                                        //     const cost = params.value[1];
                                        //     if (cost > 30000) return 'green';
                                        //     else return 'red';
                                        // },
                                        color: 'rgb(130, 85, 236)',
                                        borderColor: 'rgb(130, 85, 236)'
                                    },
                                    areaStyle: {
                                        color: new graphic.LinearGradient(0, 0, 0, 1, [
                                            {
                                                offset: 0,
                                                color: 'rgba(130, 85, 236, 0.7)'
                                            },
                                            {
                                                offset: 1,
                                                color: 'rgba(130, 85, 236, 0.2)'
                                            }
                                        ])
                                    }
                                }
                            ]
                        };
                    }

                    const mcChartData = this._computeChartData(response, 'marketCap');
                    if (!mcChartData.length) this.isMcChartValid = false;
                    else {
                        // Prepare Market Cap Chart Configuration
                        this.mcChartOptions = {
                            backgroundColor: 'transparent',
                            nameTextStyle: {
                                fontWeight: 'bold'
                            },
                            xAxis: {
                                type: 'time',
                                name: 'Time',
                                axisLabel: {
                                    show: true,
                                    hideOverlap: true,
                                    formatter: {
                                        year: '{yyyy}',
                                        month: "{MMM}'{yy}",
                                        day: '{dd}',
                                        week: '{ee}'
                                    }
                                }
                            },
                            yAxis: {
                                scale: true,
                                name: 'Market Cap',
                                nameTextStyle: {
                                    fontWeight: 'bold'
                                },
                                type: 'value',
                                splitNumber: 4,
                                boundaryGap: ['10%', '10%'],
                                axisLabel: {
                                    show: true,
                                    fontSize: 11,
                                    hideOverlap: true,
                                    fontWeight: 500,
                                    formatter: (value, _index) => {
                                        if (value > 1000) {
                                            const placeValue: PlaceValue = getPlaceValue(value);
                                            return `${this.placeValuePipe.transform(
                                                value,
                                                placeValue
                                            )} ${this.getPlaceValueNotation(placeValue)}`;
                                        } else return `${value.toFixed(this.fixedPrecision)}`;
                                    }
                                }
                            },
                            tooltip: {
                                show: true,
                                trigger: 'axis',
                                showContent: true,
                                textStyle: {
                                    fontSize: 12,
                                    color: this.isDarkMode ? '#EFEFEF' : '#212529'
                                },
                                borderWidth: 1,
                                borderRadius: 4,
                                borderColor: this.isDarkMode ? '#525355' : '#a0a7b8',
                                backgroundColor: this.isDarkMode ? '#090909' : '#FFFFFF',
                                formatter: params => this.getToolTipHtml(params[0])
                            },
                            dataZoom: [
                                {
                                    type: 'slider',
                                    start: 0,
                                    end: 100,
                                    labelFormatter: (value, valueInStr) => new Date(valueInStr).toLocaleString(),
                                    textStyle: {
                                        width: '68',
                                        overflow: 'break',
                                        show: true,
                                        fontSize: 11,
                                        lineHeight: 14
                                    },
                                    backgroundColor: 'transparent',
                                    selectedDataBackground: {
                                        lineStyle: {
                                            width: 1,
                                            color: 'rgb(130, 85, 236)'
                                        },
                                        areaStyle: {
                                            color: new graphic.LinearGradient(0, 0, 0, 1, [
                                                {
                                                    offset: 0,
                                                    color: 'rgba(130, 85, 236, 0.7)'
                                                },
                                                {
                                                    offset: 1,
                                                    color: 'rgba(130, 85, 236, 0.2)'
                                                }
                                            ])
                                        }
                                    }
                                }
                            ],
                            toolbox: {
                                feature: {
                                    restore: {
                                        emphasis: {
                                            iconStyle: {
                                                borderColor: 'rgba(139, 55, 247, 1)',
                                                textFill: 'rgba(139, 55, 247, 1)'
                                            }
                                        }
                                    },
                                    saveAsImage: {
                                        emphasis: {
                                            iconStyle: {
                                                borderColor: 'rgba(139, 55, 247, 1)',
                                                textFill: 'rgba(139, 55, 247, 1)'
                                            }
                                        }
                                    },
                                    dataZoom: {
                                        emphasis: {
                                            iconStyle: {
                                                borderColor: 'rgba(139, 55, 247, 1)',
                                                textFill: 'rgba(139, 55, 247, 1)'
                                            }
                                        }
                                    }
                                }
                            },
                            series: [
                                {
                                    name: 'Market Cap',
                                    type: 'line',
                                    smooth: false,
                                    sampling: 'lttb',
                                    symbol: 'none',
                                    connectNulls: false,
                                    data: mcChartData,
                                    emphasis: {
                                        focus: 'series'
                                    },
                                    lineStyle: {
                                        width: 3,
                                        color: 'rgb(130, 85, 236)'
                                    },
                                    itemStyle: {
                                        color: 'rgb(130, 85, 236)',
                                        borderColor: 'rgb(130, 85, 236)'
                                    }
                                }
                            ]
                        };
                    }
                }
            }
        });
    }

    private _computeChartData(
        historicalData: HistoricalData[],
        type: 'price' | 'marketCap' | 'circulatingSupply'
    ): Array<[number, number]> {
        if (type === 'price') {
            return historicalData
                .filter(data => data.price !== null && data.price !== 0) // filter out null and 0 values
                .map(data => [Date.parse(data.time), data.price as number]);
        } else if (type === 'marketCap') {
            return historicalData
                .filter(data => data.market_cap !== null && data.market_cap !== 0) // filter out null and 0 values
                .map(data => [Date.parse(data.time), data.market_cap as number]);
        } else {
            return historicalData
                .filter(data => data.circulating_supply !== null && data.circulating_supply !== 0) // filter out null and 0 values
                .map(data => [Date.parse(data.time), data.circulating_supply as number]);
        }
    }

    private getPlaceValueNotation(placeValue: PlaceValue): 'B' | 'M' | 'K' | '' {
        let notation: 'B' | 'M' | 'K' | '' = '';
        if (placeValue === 'billion') notation = 'B';
        else if (placeValue === 'million') notation = 'M';
        else if (placeValue === 'thousand') notation = 'K';
        return notation;
    }

    private getToolTipHtml(param: {
        marker: string;
        seriesName: string;
        axisValue: number;
        value: [number, number];
    }): string {
        // Get Price, Marker HTML and Series Name
        const price: number = param.value[1];
        const markerHtml: string = param.marker;
        const seriesName: string = param.seriesName;

        // Get Time in nano seconds ~ param.axisValue, prepare the date Object from it and get the date in string
        const dateObj: Date = new Date(param.axisValue);
        const dateInStr: string = `${dateObj.toLocaleString()}`;

        return `${dateInStr} <br /> <div class="mt-1">${markerHtml} ${seriesName}: <b>${price}</b></div>`;
    }

    private setLoadConfigTheme(): void {
        this.eChartLoadTheme = {
            fontSize: 14,
            lineWidth: 4,
            spinnerRadius: 12,
            fontWeight: 'bold',
            text: 'Loading data...',
            maskColor: this.isDarkMode ? 'rgba(41, 41, 43, 0.6)' : 'rgba(255, 255, 255, 0.8)',
            textColor: this.isDarkMode ? '#efefef' : '#212529',
            color: this.isDarkMode ? '#efefef' : 'rgb(130, 85, 236)'
        };
    }

    ngOnDestroy(): void {
        this.themeSub.unsubscribe();
    }
}
